import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '../components/ui/accordion';
import { Search, ArrowLeft, X } from 'lucide-react';
import { Logo } from '../components/Logo';
import Footer from '../components/Footer';

const FAQ_DATA = {
    workers: {
        label: 'Workers',
        items: [
            {
                q: 'What documents do I need to appear in the talent pool?',
                a: 'To appear in the GulfBridge talent pool and become visible to employers, you will need to complete your profile with your basic information and upload a few key documents. These include a clear profile photo, a valid passport, and your Interpol/Police Clearance letter. Once these are in place, your profile will become visible to employers. You can further strengthen your profile by adding a medical report, a short video introduction, and any additional supporting documents such as certificates or recommendation letters — though these are not required to get started.',
            },
            {
                q: 'Why am I not showing up in the talent pool yet?',
                a: 'If your profile is not yet visible in the talent pool, it is likely because one or more required profile items are still incomplete. Please check that you have filled in all your basic profile information and that all required documents have been successfully uploaded. You can check your profile completion status from your dashboard. If everything appears complete and you are still not showing up, please reach out to our support team and we will look into it for you.',
            },
            {
                q: 'Can I receive offers from multiple employers at the same time?',
                a: 'Yes. While your profile is active, employers can discover you and send you job offers. You may receive offers from more than one employer and you are free to review each one carefully before making any decision. We recommend taking your time to consider all your options. Once you make a decision on an offer, please note that this is a commitment and the process moves forward from there. Choose thoughtfully.',
            },
            {
                q: 'What happens after I accept an offer?',
                a: 'Accepting an offer is the beginning of your journey with your new employer. Once you accept, you move into the next phase of the process, which involves your visa application. You will be able to track the progress of your visa from your dashboard as your employer manages the necessary steps. Once your visa is ready, you will be notified and guided on what to do next to receive your travel documents.',
            },
            {
                q: 'What is the document access fee and when do I pay it?',
                a: 'The document access fee is a one-time fee that applies at the final stage of your hiring journey, after your visa has been approved and your travel documents are ready. You will be notified in your dashboard when it is time to make this payment. Once completed, you will be able to access, download, and print your travel documents directly from GulfBridge. It is non-refundable once your documents have been accessed.',
            },
            {
                q: 'Can I upload my own medical report or do I have to book an appointment?',
                a: 'Both options are available to you. If you already have a valid medical report, you can upload it directly to your profile at any time. If you do not have one yet, GulfBridge gives you the option to book a medical examination through one of our partnered medical facilities. You can browse available partners, choose a convenient time, and confirm your appointment through the platform. Whichever route you choose, having a medical report on your profile is a positive signal to employers reviewing your application.',
            },
            {
                q: 'What does each visa status mean — Submitted, Processing, Approved?',
                a: 'Your visa tracking section keeps you informed at every stage of your visa application. Submitted means your employer has formally initiated your visa application and it is now in the hands of the relevant authorities. Processing means your application is actively being reviewed and moving through the necessary steps — timeframes at this stage vary depending on the destination country. Approved means your visa has been granted and your travel documents are being prepared. You will receive a notification when your visa is approved and your next steps will be clearly shown in your dashboard.',
            },
            {
                q: 'What if my visa gets rejected?',
                a: 'GulfBridge supports the hiring and documentation process, but visa decisions are made entirely by the immigration authorities of the destination country. We do not have any influence over these decisions and cannot guarantee visa approval for any applicant. If your visa application is unsuccessful, we encourage you to communicate with your employer through the platform to understand the reason and explore your options. Ensuring that all your submitted documents are accurate, complete, and valid is the best way to support a successful application.',
            },
        ],
    },
    employers: {
        label: 'Employers',
        items: [
            {
                q: 'How do I post a job and send offers?',
                a: 'To post a job, go to your employer dashboard and navigate to the Job Listings section. Click "Create Job" and fill in the details — job title, salary and currency, benefits, location, country, and a description of the role. Save the listing to add it to your jobs. To send an offer, browse the talent pool, open a worker\'s profile, and click "Send Offer." You will select which of your saved jobs to offer that worker. They will receive the offer with all the job details you entered. You can manage multiple job listings and send offers from any of them at any time.',
            },
            {
                q: 'Can I send offers to multiple workers at once?',
                a: 'Yes. You can send job offers to as many workers as you are interested in. There is no restriction on how many candidates you can approach. We encourage you to be genuine and professional in your outreach — sending offers only to workers you are seriously considering makes for a better experience for everyone on the platform.',
            },
            {
                q: 'Can I view worker documents before sending an offer?',
                a: 'Yes. When you open a worker\'s full profile from the talent pool, you can view all the documents they have chosen to share — including identity documents, any medical report on file, a video introduction if they have recorded one, and any additional supporting materials they have uploaded. This gives you the information you need to make a confident and informed hiring decision before reaching out.',
            },
            {
                q: 'What happens after a worker accepts my offer?',
                a: 'Once a worker accepts your offer, they are confirmed as your candidate and you can begin the next phase of the process. Their status in your candidate tracking section will update accordingly. From your dashboard, you will manage the visa and travel documentation steps for that worker, keeping them informed of progress along the way. The platform will guide you through each stage and notify the worker at key milestones.',
            },
            {
                q: 'How do I manage visa and travel documents for a worker?',
                a: 'In your employer dashboard, navigate to the Visa and Travel Management section. Find the relevant worker and open their record. From there you can update their visa status as it progresses, upload their visa document and flight ticket, and optionally enter their expected travel date. Your dashboard includes a travel tracker that gives you an at-a-glance view of all your workers in this stage, so you can easily monitor who is travelling and when without having to open each record individually.',
            },
            {
                q: 'Is there a cost to use GulfBridge as an employer?',
                a: 'GulfBridge is currently free for employers. You can post jobs, browse the talent pool, view worker profiles and documents, send offers, and manage the full hiring and documentation process at no cost. We may introduce premium employer features in the future — registered employers will be notified of any changes well in advance.',
            },
        ],
    },
    medical: {
        label: 'Medical Partners',
        items: [
            {
                q: 'How do I set my available appointment slots?',
                a: 'Log in to your medical partner dashboard and go to the Availability section. From there you can add the dates and time slots when you are available to conduct medical examinations. Workers searching for appointments will see your availability and book accordingly. Keep your availability updated regularly to ensure workers can find and book with you. If no slots are set, you will not appear as a bookable option for workers.',
            },
            {
                q: 'How does my commission get calculated?',
                a: 'Your commission rate is agreed upon when your account is set up on GulfBridge. For every medical examination you complete and report on, your earnings are calculated based on your applicable rate. You can view a full breakdown of your earnings — including completed appointments, amounts due, and upcoming expected income — from your earnings dashboard. GulfBridge handles the collection of all appointment fees and distributes your earnings to you on a regular schedule.',
            },
            {
                q: 'When do I receive my payouts?',
                a: 'GulfBridge collects appointment fees and manages the distribution of earnings to medical partners. Your dashboard shows you a clear picture of what you have earned and what is pending payout. Payouts are processed by the GulfBridge team on a scheduled basis. If you have a specific query about your payout status or timing, please contact our support team and we will be happy to assist.',
            },
            {
                q: 'What happens after I upload a medical report?',
                a: 'Once you upload a completed medical report for a worker, it is securely attached to their GulfBridge profile and becomes part of their verified documentation. The worker is notified that their report is available. Your earnings dashboard will also update to reflect the completed appointment. You do not need to take any further action — the platform handles the rest.',
            },
        ],
    },
    general: {
        label: 'General',
        items: [
            {
                q: 'What countries does GulfBridge currently support?',
                a: 'GulfBridge welcomes blue collar workers from around the world who are seeking employment in the Gulf region. Our platform connects you directly with Gulf-based employers across the UAE, Saudi Arabia, Qatar, Kuwait, Bahrain, and Oman. Workers from all countries are welcome to register and create a profile.',
            },
            {
                q: 'Is GulfBridge responsible for visa approvals or rejections?',
                a: 'No. GulfBridge is a hiring and documentation platform. We facilitate connections between employers and workers and support the documentation process, but we are not an immigration authority and have no involvement in visa approval decisions. These decisions rest entirely with the immigration authorities of the destination country. GulfBridge does not guarantee visa approval for any applicant and bears no liability for outcomes related to visa applications. We strongly encourage all parties to ensure that submitted documents are accurate, complete, and valid.',
            },
            {
                q: 'How does GulfBridge handle my personal data?',
                a: 'GulfBridge is committed to protecting the privacy and security of your personal information. We collect only the data necessary to operate the platform and facilitate the hiring process. Your information is handled responsibly, stored securely, and is only accessible to the relevant parties involved in your hiring journey. We do not sell your data to third parties. You have the right to request access to your data or ask for it to be removed at any time by contacting our support team. For full details on how we manage your information, please read our Privacy Policy available in the footer.',
            },
            {
                q: 'How do I contact support?',
                a: (
                    <>
                        If you have a question or issue not covered in this FAQ, our support team is here to help. You can reach us through our{' '}
                        <Link to="/contact" className="text-zinc-900 underline font-semibold hover:opacity-70" data-testid="faq-inline-contact-link">Contact form</Link>
                        , available from your dashboard or via the link below this FAQ. When writing to us, please include your registered email address, your account type (Worker, Employer, or Medical Partner), and a clear description of your issue so we can assist you as quickly as possible. We aim to respond to all enquiries within 1 to 2 business days.
                    </>
                ),
            },
        ],
    },
};

const TAB_KEYS = ['workers', 'employers', 'medical', 'general'];

export default function FAQPage() {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [activeTab, setActiveTab] = useState('workers');

    const trimmedSearch = search.trim().toLowerCase();
    const isSearching = trimmedSearch.length > 0;

    // When searching, build flat results across all tabs (matching question text only)
    const searchResults = useMemo(() => {
        if (!isSearching) return [];
        const results = [];
        for (const key of TAB_KEYS) {
            for (const item of FAQ_DATA[key].items) {
                if (item.q.toLowerCase().includes(trimmedSearch)) {
                    results.push({ ...item, tab: key, tabLabel: FAQ_DATA[key].label });
                }
            }
        }
        return results;
    }, [trimmedSearch, isSearching]);

    return (
        <div className="min-h-screen bg-white" data-testid="faq-page">
            {/* Header bar */}
            <header className="border-b border-zinc-200 bg-white sticky top-0 z-10">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 text-sm font-semibold"
                        data-testid="faq-back-btn"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </button>
                    <button
                        onClick={() => navigate('/')}
                        className="hover:opacity-70 transition-opacity"
                        data-testid="faq-logo"
                        aria-label="GulfBridge home"
                    >
                        <Logo size="md" />
                    </button>
                </div>
            </header>

            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 w-full flex-1">
                {/* Title */}
                <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight" data-testid="faq-title">
                    Frequently Asked Questions
                </h1>
                <p className="text-sm text-zinc-500 mt-3 max-w-2xl" data-testid="faq-subtitle">
                    Find answers to common questions about using GulfBridge. Select your role below to get started.
                </p>

                {/* Search */}
                <div className="relative mt-6 sm:mt-8">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                        type="text"
                        placeholder="Search for a question..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="pl-10 pr-10 h-12 rounded-none border-zinc-300 focus-visible:ring-zinc-900"
                        data-testid="faq-search-input"
                    />
                    {search && (
                        <button
                            onClick={() => setSearch('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-900"
                            aria-label="Clear search"
                            data-testid="faq-search-clear"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>

                {/* Search results OR tabs */}
                {isSearching ? (
                    <div className="mt-6" data-testid="faq-search-results">
                        {searchResults.length === 0 ? (
                            <div className="border border-zinc-200 p-8 text-center" data-testid="faq-no-results">
                                <p className="text-sm text-zinc-500">No results found. Try a different search term.</p>
                            </div>
                        ) : (
                            <Accordion type="single" collapsible className="w-full divide-y divide-zinc-100 border-y border-zinc-100">
                                {searchResults.map((item, idx) => (
                                    <AccordionItem key={`${item.tab}-${idx}`} value={`search-${idx}`} className="border-0">
                                        <AccordionTrigger
                                            className="hover:no-underline py-4 text-left"
                                            data-testid={`faq-result-trigger-${idx}`}
                                        >
                                            <div className="flex flex-col items-start gap-2 pr-4 w-full">
                                                <span className="text-[10px] tracking-[0.15em] uppercase font-bold bg-zinc-100 text-zinc-600 px-2 py-0.5" data-testid={`faq-result-tag-${idx}`}>
                                                    {item.tabLabel}
                                                </span>
                                                <span className="text-base font-semibold leading-snug">{item.q}</span>
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent className="pb-4">
                                            <p className="text-sm text-zinc-600 leading-relaxed" data-testid={`faq-result-content-${idx}`}>{item.a}</p>
                                        </AccordionContent>
                                    </AccordionItem>
                                ))}
                            </Accordion>
                        )}
                    </div>
                ) : (
                    <div className="mt-6" data-testid="faq-tabs-wrapper">
                        <Tabs value={activeTab} onValueChange={setActiveTab}>
                            {/* Mobile: horizontal scroll. sm+: 4 cols */}
                            <div className="overflow-x-auto -mx-4 sm:mx-0">
                                <TabsList
                                    className="w-max sm:w-full sm:grid sm:grid-cols-4 rounded-none h-auto bg-transparent border-b border-zinc-200 p-0 mx-4 sm:mx-0 inline-flex"
                                    data-testid="faq-tabs-list"
                                >
                                    {TAB_KEYS.map(key => (
                                        <TabsTrigger
                                            key={key}
                                            value={key}
                                            className="rounded-none whitespace-nowrap border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-zinc-900 text-zinc-500 px-4 py-3 text-sm font-semibold"
                                            data-testid={`faq-tab-${key}`}
                                        >
                                            {FAQ_DATA[key].label}
                                        </TabsTrigger>
                                    ))}
                                </TabsList>
                            </div>

                            {TAB_KEYS.map(key => (
                                <TabsContent key={key} value={key} className="mt-4">
                                    <Accordion
                                        type="single"
                                        collapsible
                                        className="w-full divide-y divide-zinc-100 border-y border-zinc-100"
                                        data-testid={`faq-accordion-${key}`}
                                    >
                                        {FAQ_DATA[key].items.map((item, idx) => (
                                            <AccordionItem key={idx} value={`${key}-${idx}`} className="border-0">
                                                <AccordionTrigger
                                                    className="hover:no-underline py-4 text-left text-base font-semibold leading-snug"
                                                    data-testid={`faq-q-${key}-${idx}`}
                                                >
                                                    {item.q}
                                                </AccordionTrigger>
                                                <AccordionContent className="pb-4">
                                                    <p
                                                        className="text-sm text-zinc-600 leading-relaxed"
                                                        data-testid={`faq-a-${key}-${idx}`}
                                                    >
                                                        {item.a}
                                                    </p>
                                                </AccordionContent>
                                            </AccordionItem>
                                        ))}
                                    </Accordion>
                                </TabsContent>
                            ))}
                        </Tabs>
                    </div>
                )}

                {/* Footer */}
                <div className="mt-12 pt-6 border-t border-zinc-100 text-center">
                    <p className="text-xs text-zinc-500 mb-3">
                        Can&apos;t find your question?
                    </p>
                    <button
                        type="button"
                        onClick={() => navigate('/contact')}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white text-xs font-semibold uppercase tracking-wider hover:bg-zinc-800"
                        data-testid="faq-contact-btn"
                    >
                        Contact Us
                    </button>
                </div>
            </div>
            <Footer />
        </div>
    );
}
